// ---------------------------------------------------------------
// Tiny bilingual (English / Spanish) helper for the TagAlong app.
// The chosen language is saved so it persists, and the future customer
// login screen can call setLang() the moment they pick one.
// Usage:  const t = useT();  ...  t('askTitle')
// ---------------------------------------------------------------
import { useState, useEffect } from 'react';

const KEY = 'tagalong-lang';
const listeners = new Set();

export const getLang = () => {
  try { return localStorage.getItem(KEY) || 'en'; } catch { return 'en'; }
};
export const setLang = (lang) => {
  try { localStorage.setItem(KEY, lang); } catch { /* ignore */ }
  listeners.forEach((fn) => fn(lang));
};

// React hook: re-renders the component when the language changes anywhere.
export function useLang() {
  const [lang, set] = useState(getLang());
  useEffect(() => { const fn = (l) => set(l); listeners.add(fn); return () => listeners.delete(fn); }, []);
  return [lang, setLang];
}
export function useT() {
  const [lang] = useLang();
  return (key, ...args) => translate(lang, key, ...args);
}

const DICT = {
  tagline: { en: 'The car that talks back.', es: 'El auto que te responde.' },
  subtitle: { en: 'Live status, daily driving, and ask the car anything.', es: 'Estado en vivo, manejo diario y pregúntale lo que sea al auto.' },
  tripMap: { en: '🗺️ Trip map', es: '🗺️ Mapa de viaje' },
  selectCar: { en: 'Select a car', es: 'Elige un auto' },
  byAccount: { en: '👥 By account', es: '👥 Por cuenta' },
  allDevices: { en: '📋 All devices', es: '📋 Todos los equipos' },
  unassigned: { en: 'Unassigned', es: 'Sin asignar' },
  showAccountCars: { en: "Show this account's cars on the page", es: 'Mostrar los autos de esta cuenta en la página' },
  cars: { en: 'cars', es: 'autos' },
  car: { en: 'car', es: 'auto' },
  tapToOpen: { en: 'the open one is below · tap another to switch', es: 'el abierto está abajo · toca otro para cambiar' },
  thisAccount: { en: 'This account', es: 'Esta cuenta' },
  switchToCar: { en: 'Switch to this car', es: 'Cambiar a este auto' },
  open: { en: '▸ open', es: '▸ abrir' },
  on: { en: 'ON', es: 'ENCENDIDO' },
  off: { en: 'OFF', es: 'APAGADO' },
  show: { en: '▸ show', es: '▸ ver' },
  hide: { en: '▾ hide', es: '▾ ocultar' },
  showThisCar: { en: 'Show this car', es: 'Mostrar este auto' },
  hideThisCar: { en: 'Hide this car', es: 'Ocultar este auto' },
  rename: { en: 'Rename this device', es: 'Renombrar este equipo' },
  waitingSignal: { en: 'Waiting for signal…', es: 'Esperando señal…' },
  locating: { en: 'Locating address…', es: 'Localizando dirección…' },
  collapsedNote: { en: 'is collapsed — click its name to bring everything back.', es: 'está contraído — haz clic en su nombre para traer todo de vuelta.' },
  clickToEnlarge: { en: 'Click to enlarge', es: 'Haz clic para ampliar' },
  clickToClose: { en: 'Click anywhere to close', es: 'Haz clic en cualquier lugar para cerrar' },
  // stats
  milesToday: { en: 'Miles today', es: 'Millas hoy' },
  topSpeed: { en: 'Top speed', es: 'Vel. máxima' },
  trips: { en: 'Trips', es: 'Viajes' },
  hardBrakes: { en: 'Hard brakes', es: 'Frenadas bruscas' },
  hardAccel: { en: 'Hard accel', es: 'Aceleradas bruscas' },
  speeding: { en: 'Speeding', es: 'Exceso de vel.' },
  // places
  placesTitle: { en: '📍 Places & arrivals', es: '📍 Lugares y llegadas' },
  placeName: { en: 'Name (Home, School)', es: 'Nombre (Casa, Escuela)' },
  address: { en: 'Address', es: 'Dirección' },
  add: { en: 'Add', es: 'Agregar' },
  noPlaces: { en: 'No places saved for this car yet — add Home or School above to get arrival alerts.', es: 'Aún no hay lugares guardados para este auto — agrega Casa o Escuela arriba para recibir avisos de llegada.' },
  placesScopeHint: { en: '📍 Places you add here alert you for this car only. To save a place for every car at once, use the Vehicles tab.', es: '📍 Los lugares que agregues aquí te avisan solo para este auto. Para guardar un lugar para todos los autos a la vez, usa la pestaña Vehículos.' },
  recentArrivals: { en: 'Recent arrivals / departures', es: 'Llegadas / salidas recientes' },
  noneRecent: { en: 'None in the last 24 hours.', es: 'Ninguna en las últimas 24 horas.' },
  removePlace: { en: 'Remove place', es: 'Quitar lugar' },
  // alerts
  alertsTitle: { en: '🔔 Alerts', es: '🔔 Alertas' },
  // ask
  askTitle: { en: '💬 Ask TagAlong', es: '💬 Pregúntale a TagAlong' },
  askPlaceholder: { en: 'Ask TagAlong…', es: 'Pregúntale a TagAlong…' },
  askButton: { en: 'Ask', es: 'Preguntar' },
  greeting: { en: "Hi, I'm TagAlong 🚗 — ask me anything about your car: where it is, how it's driving, fuel, check-engine, trip history, or set an alert (“let me know when it gets to school”).", es: 'Hola, soy TagAlong 🚗 — pregúntame lo que sea sobre tu auto: dónde está, cómo va, gasolina, check-engine, historial de viajes, o crea una alerta (“avísame cuando llegue a la escuela”).' },
  seeOnMap: { en: '🗺️ See it on your map', es: '🗺️ Verlo en tu mapa' },
  checking: { en: '…checking', es: '…consultando' },
  footer: { en: "TagAlong — answers pull from your car's live data and trip history. Two-way texting and arrival push notifications turn on when the service goes live on the server.", es: 'TagAlong — las respuestas vienen de los datos en vivo y el historial de tu auto. Los mensajes de texto y las notificaciones de llegada se activan cuando el servicio esté en línea en el servidor.' },
  language: { en: 'Language', es: 'Idioma' },
  // home / bento
  addVehicle: { en: '➕ Add a vehicle', es: '➕ Agregar un vehículo' },
  driverScore: { en: 'Driver score', es: 'Puntaje de manejo' },
  today: { en: 'today', es: 'hoy' },
  tripsTopSpeed: { en: 'trips · top', es: 'viajes · máx' },
  signOut: { en: '🚪 Sign out', es: '🚪 Cerrar sesión' },
  myProfile: { en: '⚙️ My profile', es: '⚙️ Mi perfil' },
  // curfew
  curfewTitle: { en: '🌙 Smart curfew', es: '🌙 Toque de queda' },
  curfewOn: { en: 'on', es: 'activo' },
  curfewOff: { en: 'off', es: 'inactivo' },
  curfewExplain: { en: 'After curfew, if the car is heading home you get a calm “heading home — ETA 11:15”. If it’s out and moving away, you get a real alert. Direction matters.', es: 'Después del toque de queda, si el auto va camino a casa recibes un aviso tranquilo “va a casa — llega 11:15”. Si está fuera y alejándose, recibes una alerta real. La dirección importa.' },
  curfewEnable: { en: 'Turn on curfew for this car', es: 'Activar toque de queda para este auto' },
  curfewFrom: { en: 'From', es: 'Desde' },
  curfewUntil: { en: 'Until', es: 'Hasta' },
  curfewDays: { en: 'Days', es: 'Días' },
  curfewHome: { en: 'Home (a saved place)', es: 'Casa (un lugar guardado)' },
  curfewChooseHome: { en: 'Choose home…', es: 'Elige casa…' },
  curfewNeedPlace: { en: 'Add a saved place above first (e.g. “Home”), then pick it here.', es: 'Primero agrega un lugar guardado arriba (ej. “Casa”), luego elígelo aquí.' },
  curfewSave: { en: 'Save curfew', es: 'Guardar toque de queda' },
  // speed-limit alerts
  spdTitle: { en: '🚧 Speed-limit alerts', es: '🚧 Alertas de límite de velocidad' },
  spdExplain: { en: 'Alerts when this car goes over the posted limit on the road it’s on (e.g. “55 in a 35”), not just a fixed number. Your regular speeding alert stays on.', es: 'Avisa cuando este auto pasa el límite señalizado de la vía donde va (ej. “55 en zona de 35”), no solo un número fijo. Tu alerta normal de velocidad sigue activa.' },
  // built-in alerts panel
  builtInAlerts: { en: 'Built-in alerts', es: 'Alertas incluidas' },
  yourCustomAlerts: { en: 'Your custom alerts', es: 'Tus alertas personalizadas' },
  noneYet: { en: 'None yet — build one below.', es: 'Ninguna todavía — crea una abajo.' },
  createAlert: { en: 'Create your own alert', es: 'Crea tu propia alerta' },
  alertNameIt: { en: 'Name it', es: 'Nómbrala' },
  alertNamePh: { en: 'e.g. Speeding on the highway', es: 'ej. Exceso de velocidad en la autopista' },
  alertWhen: { en: 'When', es: 'Cuando' },
  alertIs: { en: 'Is', es: 'Esté' },
  alertValue: { en: 'Value', es: 'Valor' },
  alertOver: { en: 'over', es: 'por encima de' },
  alertUnder: { en: 'under', es: 'por debajo de' },
  addAlert: { en: '+ Add alert', es: '+ Agregar alerta' },
  saveAlerts: { en: 'Save alerts', es: 'Guardar alertas' },
  inAppChip: { en: '🔔 In-app', es: '🔔 En la app' },
  alertsDelivery: { en: '🔔 Alerts are delivered in the app (banner + spoken + notifications) — free, no phone plan needed.', es: '🔔 Las alertas llegan en la app (banner + voz + notificaciones) — gratis, sin plan telefónico.' },
  inAppNote: { en: 'In-app delivery is on now (banner, voice & notifications).', es: 'La entrega en la app está activa (banner, voz y notificaciones).' },
};

export function translate(lang, key) {
  const entry = DICT[key];
  if (!entry) return key;
  return entry[lang] || entry.en || key;
}
